import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getUserData } from 'modules/users/actions';
import Avatar from 'components/Avatar';
import apiFetch from 'utils/apiFetch';
import { sessionService } from 'redux-react-session';
import LoadingComponent from 'components/LoadingComponent';
import TextField from '@material-ui/core/TextField';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import InputLabel from '@material-ui/core/InputLabel';
import AlertModal from 'components/FeedbackModal';
import { ROUTE_PROFILE } from 'constants/urls';
import {
  EditionForm, LoadingWrapper,
} from './styles';

const TAB_PROFILE = 'tab_profile';
const TAB_PASSWORD = 'tab_password';

function ProfileEdit(props) {
  const {
    literals, setBreadcrumb, resetAll, actualUser, app, history,
  } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [selectedTab, setSelectedTab] = useState(TAB_PROFILE);
  const [avatar, setAvatar] = useState(actualUser.avatar);
  const [name, setName] = useState(actualUser.name);
  const [surname, setSurname] = useState(actualUser.surname);
  const [gender, setGender] = useState(actualUser.gender);
  const [overview, setOverview] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [errors, setErrors] = useState([]);
  const [showPasswordChangedModal, setShowPasswordChangedModal] = useState(false);

  // Breadcrumb
  useEffect(() => {
    const breadcrumb = [
      {
        color: '#ffffff',
        text: literals.editProfile,
      },
    ];

    setBreadcrumb(breadcrumb.slice());
    resetAll();
  }, []);

  // User data
  useEffect(() => {
    if (actualUser && actualUser.id) {
      setIsLoading(true);
      getUserData(actualUser.id)
        .then((userData) => {
          setIsLoading(false);
          setOverview(userData.overview);
        })
        .catch(() => {
          setIsLoading(false);
        });
    }
  }, [actualUser]);

  // Change avatar
  const uploadImage = (data, nameFile) => {
    const params = {
      data,
      name: nameFile,
    };

    apiFetch('POST', { endPoint: 'upload-file' }, params)
      .then((response) => {
        setErrors([]);
        setAvatar(response.url);
      })
      .catch(() => {
        errors.push(literals.errorUploadingImage);
      });
  };

  const handleFileUpload = (event) => {
    if (event.target.files[0] !== undefined) {
      const file = event.target.files[0];
      const { name: nameFile } = file;

      const reader = new FileReader();
      reader.onloadend = () => {
        uploadImage(reader.result, nameFile);
      };
      reader.readAsDataURL(file);
    }
  };

  // Errors
  const removeErrors = () => {
    setErrors([]);
  };

  // Change name
  const handleChangeName = (event) => {
    setName(event.currentTarget.value);
  };

  // Change surname
  const handleChangeSurname = (event) => {
    setSurname(event.currentTarget.value);
  };

  // Change gender
  const handleChangeGender = (event) => {
    setGender(event.target.value);
  };

  // Change overview
  const handleChangeOverview = (event) => {
    setOverview(event.target.value);
  };

  // Change old password
  const handleChangeOldPassword = (event) => {
    setOldPassword(event.target.value);
  };

  // Change new password
  const handleChangeNewPassword = (event) => {
    setNewPassword(event.target.value);
  };

  // Change repeat password
  const handleChangeRepeatPassword = (event) => {
    setRepeatPassword(event.target.value);
  };

  // Tabs
  const handleViewProfile = () => {
    setSelectedTab(TAB_PROFILE);
  };

  const handleViewPassword = () => {
    setSelectedTab(TAB_PASSWORD);
  };
  // Main buttons
  const checkForm = () => {
    return name.trim() && surname.trim();
  };

  const checkFormPassword = () => {
    return newPassword && oldPassword && repeatPassword;
  };

  const handleCancel = () => {
    history.push(ROUTE_PROFILE);
  };

  const handleSubmit = () => {
    if (!checkForm()) {
      return false;
    }

    const params = {
      name,
      surname,
      avatar,
      gender,
      overview,
    };

    if (gender !== 'male' && gender !== 'female') {
      params.gender = '';
    }

    setIsLoading(true);
    apiFetch('PUT', { endPoint: 'users' }, params)
      .then((response) => {
        const userData = {
          ...actualUser,
          ...response,
        };
        sessionService.saveUser(userData);
        handleCancel();
      })
      .catch((error) => {
        setIsLoading(false);
        errors.push(error.response.data.error);
        setErrors([...errors]);
      });

    return true;
  };

  const handleSubmitPassword = () => {
    if (!checkFormPassword()) {
      return false;
    }

    if (newPassword !== repeatPassword) {
      errors.push(literals.passwordsDontMatch);
    }

    if (newPassword.length < 8) {
      errors.push(literals.passwordLengthError);
    }

    setErrors([...errors]);

    if (!errors.length) {
      const params = {
        old_password: oldPassword,
        new_password: newPassword,
      };

      setIsLoading(true);
      apiFetch('POST', { endPoint: 'users/password' }, params)
        .then((response) => {
          if (!response.success) {
            errors.push(response.info.message);
            setErrors([...errors]);
          } else {
            setShowPasswordChangedModal(true);
          }
          setIsLoading(false);
        })
        .catch((error) => {
          setIsLoading(false);
          errors.push(error.response.data.error);
          setErrors([...errors]);
        });
    }

    return true;
  };

  const hidePasswordModal = () => {
    setShowPasswordChangedModal(false);
  };

  // Print functions
  const printProfileForm = () => (
    <EditionForm className='form' color={app.color1}>
      <label className='avatar_layer' htmlFor='document_file'>
        <Avatar size={150} avatar={avatar} firstname={name} id={actualUser.id} />
        <div className='btn_edit_profile'><i className='fas fa-pen' /></div>
      </label>
      <input
        type='file'
        id='document_file'
        name='document_file'
        style={{ display: 'none' }}
        onChange={handleFileUpload}
        accept='image/png, image/jpeg'
      />
      <TextField
        value={name}
        label={literals.firstname}
        required
        onChange={handleChangeName}
        className='textfield_material_ui'
      />
      <TextField
        value={surname}
        label={literals.lastname}
        required
        onChange={handleChangeSurname}
        className='textfield_material_ui'
      />
      <InputLabel
        className='label_material_ui'
        id='label_gender'
      >
        {literals.selectYourGender}
      </InputLabel>
      <Select
        value={gender}
        onChange={handleChangeGender}
        className='select_material_ui'
        labelId='label_gender'
      >
        <MenuItem value=''>{literals.selectYourGender}</MenuItem>
        <MenuItem value='male'>{literals.male}</MenuItem>
        <MenuItem value='female'>{literals.female}</MenuItem>
      </Select>
      <TextField
        value={actualUser.emailList[0]}
        label={literals.email}
        disabled
        className='textfield_material_ui disabled'
      />
      <TextField
        value={overview}
        label={literals.overview}
        onChange={handleChangeOverview}
        multiline
        rows={4}
        className='textfield_material_ui'
      />
      <div className='buttons'>
        <button className={`primary ${checkForm() ? '' : 'disabled'}`} onClick={handleSubmit}>{literals.save}</button>
        <button className='cancel' onClick={handleCancel}>{literals.cancel}</button>
      </div>
    </EditionForm>
  );

  const printPasswordForm = () => {
    return (
      <EditionForm className='form' color={app.color1}>
        {showPasswordChangedModal ? (
          <AlertModal
            onClose={hidePasswordModal}
            open
            success
            text={literals.passwordHasBeenChanged}
          />
        ) : null}
        <TextField
          value={oldPassword}
          onChange={handleChangeOldPassword}
          className='textfield_material_ui'
          label={literals.oldPassword}
          type='password'
        />
        <TextField
          value={newPassword}
          onChange={handleChangeNewPassword}
          className='textfield_material_ui'
          label={literals.newPassword}
          type='password'
        />
        <TextField
          value={repeatPassword}
          onChange={handleChangeRepeatPassword}
          className='textfield_material_ui'
          label={literals.repeatPassword}
          type='password'
        />
        <div className='buttons'>
          <button className={`primary ${checkFormPassword() ? '' : 'disabled'}`} onClick={handleSubmitPassword}>{literals.save}</button>
          <button className='cancel' onClick={handleCancel}>{literals.cancel}</button>
        </div>
      </EditionForm>
    );
  };

  const printContentTab = () => {
    switch (selectedTab) {
      case TAB_PASSWORD:
        return printPasswordForm();
      default:
        return printProfileForm();
    }
  };


  const printErrors = () => {
    if (!errors.length) {
      return null;
    }
    return (
      <AlertModal
        onClose={removeErrors}
        open
        success={false}
        text={errors.join('<br />')}
      />
    );
  };

  if (isLoading) {
    return (
      <LoadingWrapper>
        <LoadingComponent colorAnimation={app.color1} />
      </LoadingWrapper>
    );
  }

  return (
    <div>
      {printErrors()}
      <div className='tabs x2'>
        <div className={`tab ${selectedTab === TAB_PROFILE ? 'selected' : ''}`} onClick={handleViewProfile}>
          {literals.profileTab}
        </div>
        <div className={`tab ${selectedTab === TAB_PASSWORD ? 'selected' : ''}`} onClick={handleViewPassword}>
          {literals.passwordTab}
        </div>
      </div>
      {printContentTab()}
    </div>
  );
}

ProfileEdit.propTypes = {
  literals: PropTypes.object.isRequired,
  setBreadcrumb: PropTypes.func.isRequired,
  actualUser: PropTypes.object.isRequired,
  app: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
};

export default ProfileEdit;



// WEBPACK FOOTER //
// ./src/routes/ProfileEdit/components/index.jsx